import { getFilenameFromUrl } from "helpers/url";
import { getVastConfigFromTag } from "model/creative/helpers/vast";
import { MULTIPLE_IMPRESSION_TRACKERS_TEMPLATE } from "model/creative/templates/ImpressionTrackerTemplate";

export type CreativeChangeDetail = {
  label: string;
  oldValue?: string;
  newValue?: string;
};

const fileName = (url?: string) => (url ? getFilenameFromUrl(url) : undefined);

const detail = (
  label: string,
  oldValue?: string,
  newValue?: string
): CreativeChangeDetail | null =>
  (oldValue ?? "") === (newValue ?? "") ? null : { label, oldValue, newValue };

const trackersState = (markup?: string) =>
  markup?.includes(MULTIPLE_IMPRESSION_TRACKERS_TEMPLATE) ? "On" : "Off";

const PLAYABLE_ENDCARD = "Playable endcard";

// the reporter hands the vast back with every double quote stripped, hence the ["']?
const MEDIAFILE_WIDTH = /<MediaFile\b[^>]*\bwidth=["']?(\d+)/i;
const MEDIAFILE_HEIGHT = /<MediaFile\b[^>]*\bheight=["']?(\d+)/i;
const STATIC_ENDCARD_SRC = /<StaticResource[^>]*><!\[CDATA\[([^\]]+)\]\]>/i;
const HTML_ENDCARD_SRC =
  /<img[^>]*class=["']?noselect["']?[^>]*src=["']?([^"'\s>]+?)["']?\s*\/?>/i;
const HTML_RESOURCE = /<HTMLResource\b/i;
// same stripped-quote problem as the vast tag, so imageURLsFromMarkup misses it
const MARKUP_IMG_SRC = /<img[^>]*\bsrc=["']?([^"'\s>]+)/i;

const markupImage = (markup?: string) =>
  fileName(markup?.match(MARKUP_IMG_SRC)?.[1]);

const dimensions = (vastTag?: string) => {
  if (!vastTag) return undefined;

  const width = vastTag.match(MEDIAFILE_WIDTH)?.[1];
  const height = vastTag.match(MEDIAFILE_HEIGHT)?.[1];

  return width && height ? `${width}x${height}` : undefined;
};

/**
 * Only the two shapes an endcard image can actually take, instead of grabbing any
 * image url in the blob: a playable inlines its own html and the first image in
 * there is not the endcard.
 */
const endcardValue = (vastTag?: string) => {
  if (!vastTag) return undefined;

  const imageSrc =
    vastTag.match(STATIC_ENDCARD_SRC)?.[1] ??
    vastTag.match(HTML_ENDCARD_SRC)?.[1];

  if (imageSrc) return fileName(imageSrc);

  return HTML_RESOURCE.test(vastTag) ? PLAYABLE_ENDCARD : undefined;
};

/**
 * The vast/markup blobs arrive as one giant string on a single change row, so the
 * only way to say what actually changed is to parse both sides and compare.
 */
export const getVastChangeDetails = (
  oldVast?: string,
  newVast?: string
): CreativeChangeDetail[] => {
  const before = getVastConfigFromTag(oldVast);
  const after = getVastConfigFromTag(newVast);

  if (!before || !after) return [];

  const oldEndcard = endcardValue(oldVast);
  const newEndcard = endcardValue(newVast);
  const isPlayable =
    oldEndcard === PLAYABLE_ENDCARD || newEndcard === PLAYABLE_ENDCARD;

  return [
    detail("Video", fileName(before.videoUrl), fileName(after.videoUrl)),
    detail("Duration", before.duration, after.duration),
    detail("Ad title", before.adTitle, after.adTitle),
    detail("Video dimensions", dimensions(oldVast), dimensions(newVast)),
    detail(isPlayable ? "Endcard" : "Endcard image", oldEndcard, newEndcard),
  ].filter(Boolean) as CreativeChangeDetail[];
};

export const getMarkupChangeDetails = (
  oldMarkup?: string,
  newMarkup?: string
): CreativeChangeDetail[] => {
  if (!oldMarkup || !newMarkup) return [];

  return [
    detail("Image", markupImage(oldMarkup), markupImage(newMarkup)),
    detail(
      "Multiple impression trackers",
      trackersState(oldMarkup),
      trackersState(newMarkup)
    ),
  ].filter(Boolean) as CreativeChangeDetail[];
};

const DETAIL_EXTRACTORS: Array<{
  path: string;
  extract: (oldValue?: string, newValue?: string) => CreativeChangeDetail[];
}> = [
  { path: "/creative/vast", extract: getVastChangeDetails },
  { path: "/creative/markup", extract: getMarkupChangeDetails },
];

/**
 * Whatever we could not pull out of the blob is not worth showing as a diff of two
 * pages of xml/html, so these rows never fall back to printing their raw value.
 */
export const holdsEmbeddedMarkup = (fieldPath: string) =>
  DETAIL_EXTRACTORS.some((e) => e.path === fieldPath);

export const getCreativeChangeDetails = (
  fieldPath: string,
  oldValue?: string,
  newValue?: string
): CreativeChangeDetail[] | undefined => {
  const extractor = DETAIL_EXTRACTORS.find((e) => e.path === fieldPath);
  if (!extractor) return undefined;

  const details = extractor.extract(oldValue, newValue);
  return details.length ? details : undefined;
};
